'use client';

import { useEffect, useRef, useState } from 'react';

export type ImageFormatVariant =
  | 'jpg-to-png'
  | 'png-to-jpg'
  | 'webp-to-png'
  | 'webp-to-jpg'
  | 'gif-to-png';

const config: Record<
  ImageFormatVariant,
  {
    title: string;
    badge: string;
    from: string;
    to: string;
    mime: string;
    ext: string;
    fillWhite: boolean;
  }
> = {
  'jpg-to-png': {
    title: 'Convert JPG to PNG',
    badge: 'PNG',
    from: 'JPG',
    to: 'PNG',
    mime: 'image/png',
    ext: 'png',
    fillWhite: false,
  },
  'png-to-jpg': {
    title: 'Convert PNG to JPG',
    badge: 'JPG',
    from: 'PNG',
    to: 'JPG',
    mime: 'image/jpeg',
    ext: 'jpg',
    fillWhite: true,
  },
  'webp-to-png': {
    title: 'Convert WebP to PNG',
    badge: 'PNG',
    from: 'WebP',
    to: 'PNG',
    mime: 'image/png',
    ext: 'png',
    fillWhite: false,
  },
  'webp-to-jpg': {
    title: 'Convert WebP to JPG',
    badge: 'JPG',
    from: 'WebP',
    to: 'JPG',
    mime: 'image/jpeg',
    ext: 'jpg',
    fillWhite: true,
  },
  'gif-to-png': {
    title: 'Convert GIF to PNG',
    badge: 'PNG',
    from: 'GIF',
    to: 'PNG',
    mime: 'image/png',
    ext: 'png',
    fillWhite: false,
  },
};

export default function BrowserImageFormatConverter({ variant }: { variant: ImageFormatVariant }) {
  const c = config[variant];
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState('image');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  function handleFile(file: File | undefined) {
    if (!file) return;
    setFileName(file.name.replace(/\.[^.]+$/, '') || 'image');
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setImage(img);
    img.src = url;
  }

  useEffect(() => {
    if (!image || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (c.fillWhite) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(image, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) setDownloadUrl(URL.createObjectURL(blob));
      },
      c.mime,
      0.92,
    );
  }, [image, c]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{c.badge}</span>
        <h2 className="tool-live-title">{c.title}</h2>
        <p className="tool-live-subtitle">
          Upload a {c.from} image and convert it to {c.to} entirely in your browser. Nothing is
          uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose a {c.from} image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>
          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${fileName}.${c.ext}`}
                href={downloadUrl}
              >
                Download {c.to}
              </a>
            ) : null}
          </div>
          {image ? null : <p className="tool-live-panel-note">Pick an image to convert.</p>}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            Conversion runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
